import { Component } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import classNames from 'classnames';
import TimeAgo from 'timeago-react';
import { TimeUtilities, Functional } from '@wayfinder/vms-base';
import ToolTip from '../tooltip/Tooltip.jsx';
import s from './MapNoteSheet.module.scss';

const MILLISECONDS_IN_A_DAY = 8640 * 100 * 100;

export default class MapNoteComments extends Component {
    static propTypes = {
        userEmail: PropTypes.string.isRequired,
        mapNote: PropTypes.object.isRequired,
        onCommentDelete: PropTypes.func.isRequired,
    };

    renderTimeAgo = datetime => {
        // Check if date is older than a week ago
        if (Date.parse(new Date().toISOString()) - Date.parse(datetime) < MILLISECONDS_IN_A_DAY * 7) {
            return (
                <div className={s.tooltip}>
                    <TimeAgo className={s.body} datetime={datetime} live={false} />
                    <ToolTip arrowPosition="top" text={TimeUtilities.calculateDateTimeUK(datetime)} />
                </div>
            );
        }
        return <div className={s.body}>{TimeUtilities.calculateDate(datetime)}</div>;
    };

    renderHistoryComment = (email, body, date) => {
        const author = R.split('@', email)[0];
        return (
            <div key={`${body}-${date}`} className={s.historyComment}>
                <div className={classNames({ [s.author]: R.length(author) > 0 })}>{author}</div>
                <div className={s.body}>{body}&nbsp;</div>
                <div>{this.renderTimeAgo(date)}</div>
            </div>
        );
    };

    renderAttachedHeader = isPic => (
        <div className={s.pictureHeader}>
            <span className={classNames(isPic ? 'icon-image' : 'icon-PDF', s.icon)} />
            <div>attached</div>
        </div>
    );

    renderDeleteComment = commentId => (
        <button type="button" onClick={() => this.props.onCommentDelete(commentId)} className={s.removeButtonStyling}>
            <div className={classNames('icon-close', s.closeIcon)} />
        </button>
    );

    renderPicture = pic => {
        const img = new Image();
        img.src = pic;
        const overMaxWidth = img.naturalWidth > 461;
        const overMaxHeight = img.naturalHeight > 325;
        return (
            <div className={s.pic} key={pic}>
                <a href={pic} rel="noopener noreferrer" target="_blank">
                    {R.last(R.split('=', pic))}
                </a>
                <div
                    className={classNames(
                        {
                            [s.thumbnailNone]: img.naturalWidth === 0 || img.naturalHeight === 0,
                            [s.thumbnailMax]: overMaxWidth && overMaxHeight,
                            [s.thumbnailMaxWidth]: overMaxWidth && !overMaxHeight,
                            [s.thumbnailMaxHeight]: !overMaxWidth && overMaxHeight,
                        },
                        s.thumbnail,
                    )}
                >
                    <img className={s.img} src={pic} alt={pic} />
                </div>
            </div>
        );
    };

    renderPdf = pdf => (
        <a href={pdf} download>
            {R.last(R.split('=', pdf))}
        </a>
    );

    renderPostedComment = (comment, userEmail) => {
        const author = R.split('@', comment.createdBy.email)[0];
        const upload = R.head(comment.uploads);
        const isPic = !R.isEmpty(comment.uploads) && (R.endsWith('jpeg', R.toLower(upload)) || R.endsWith('jpg', R.toLower(upload)) || R.endsWith('png', R.toLower(upload)));
        const isPdf = !R.isEmpty(comment.uploads) && R.endsWith('pdf', upload);
        return (
            <div key={comment.createdAt} className={s.postedComment}>
                <div className={s.commentHeader}>
                    <div className={classNames('icon-mapnoteprofile', s.icon, s.profileIcon)} />
                    <div className={classNames({ [s.author]: R.length(author) > 0 })}>{author}</div>
                    {(isPic || isPdf) && this.renderAttachedHeader(isPic)}
                    <div className={classNames(s.body, s.datetime)}>{this.renderTimeAgo(comment.createdAt)}</div>
                    {comment.createdBy.email === userEmail && this.renderDeleteComment(comment.id)}
                </div>
                <div className={s.comment}>{comment.body}</div>
                {isPic && this.renderPicture(upload)}
                {isPdf && this.renderPdf(upload)}
            </div>
        );
    };

    renderComments = (mapNote, userEmail) => {
        const renderStyledNoteHistory = (comment, noteId) => {
            const fieldName = comment.fieldName === 'noteType' ? 'note type' : 'status';
            const mapNoteMessage = `Map note #${noteId} ${fieldName}`;
            const newValue = `${Functional.capitalizeFirst(R.replace(/([A-Z])/g, ' $1', comment.newValue))}`;
            return (
                <div key={comment.createdAt} className={s.styledHistoryComment}>
                    <div className={s.body}>{`${mapNoteMessage} changed to`}</div>
                    <div className={s.body}>
                        <span className={classNames(s[comment.newValue], s.bodyStyled)}>{newValue}</span>
                    </div>
                    <div className={s.body}>.&nbsp;</div>
                </div>
            );
        };

        const renderEachComment = comment => {
            if (comment.type === 'history') {
                const action = () => {
                    switch (comment.fieldName) {
                        case 'latitude':
                            return `Map note #${mapNote.id} moved.`;
                        case 'longitude':
                            return `Map note #${mapNote.id} moved.`;
                        case 'noteType':
                            return renderStyledNoteHistory(comment, mapNote.id);
                        case 'noteStatus':
                            return renderStyledNoteHistory(comment, mapNote.id);
                        default:
                            return `Map note #${mapNote.id} updated.`;
                    }
                };
                return this.renderHistoryComment('', action(), comment.createdAt);
            }
            return this.renderPostedComment(comment, userEmail);
        };
        return R.map(renderEachComment, mapNote.comments);
    };

    render() {
        const { mapNote, userEmail } = this.props;
        return (
            <div className={s.commentsContainer}>
                {this.renderHistoryComment(mapNote.createdBy.email, `created map note #${mapNote.id}.`, mapNote.createdAt)}
                {this.renderComments(mapNote, userEmail)}
            </div>
        );
    }
}
